import React from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { Helmet } from "react-helmet";
import nateLevineAsset from "@/assets/nate-levine.jpg.asset.json";

interface TeamMember {
  name: string;
  role: string;
  bio: string;
  initials: string;
  imageUrl?: string;
}

const TeamSection = () => {
  const teamMembers: TeamMember[] = [
    {
      name: "Gillian (Wildfire) Sowray",
      role: "Co-founder & CEO",
      bio: "Gillian (Wildfire) Sowray is a veteran product leader with 16 years of experience driving innovation at scale in climate tech (Palmetto), software (New Relic), and CPG (Keurig). She holds a MBA and a M.S. in Public Policy from Carnegie Mellon University, and a bachelors from Boston University.",
      initials: "GS",
      imageUrl: "/lovable-uploads/69c2731c-f0dc-41d3-8812-8b392d7302c2.png",
    },
    {
      name: "Jon Gamble",
      role: "Co-founder & CTO",
      bio: "Jon Gamble is a software engineer and technical leader with 15 years of experience building intelligent systems across clean tech and industrial operations. He's led engineering teams in renewable energy and architected industrial IoT solutions, with expertise spanning AI/ML engineering, backend development, and building maintainable systems that solve real operational challenges. He's also a lifelong sportsman and Eagle Scout.",
      initials: "JG",
      imageUrl: "/lovable-uploads/94ceedd1-ed55-4ca5-b295-a4696aa37c5f.png",
    },
    {
      name: "Richie Rasor",
      role: "Co-founder & COO",
      bio: "A majority of Richie's professional career has been focused on honing his skills within the sustainable energy and built environment. From team building and quality improvement strategies (SolarCity; Tesla), to process architecture and lifecycle analysis (Tesla; Palmetto), he has come across many of the hurdles and challenges that face the energy and construction sectors.",
      initials: "RR",
      imageUrl: "/lovable-uploads/26c946ec-4296-4ac9-9351-78321594ea25.png",
    },
    {
      name: "Matt Tucker",
      role: "Advisor",
      bio: "Matt Tucker is a climate tech investor and advisor. He's a multi-time founder, including Jive Software (CTO, IPO in 2011) and Koan (CEO). He's passionate about building great products, great teams, and knows far too much about clear ice. He holds a B.S. in Computer Science from the University of Iowa.",
      initials: "MT",
      imageUrl: "/lovable-uploads/523b42bc-1085-4980-9e78-c839d901d013.png",
    },
    {
      name: "Nate Levine",
      role: "Advisor",
      bio: "Nate Levine is a two time successful founder, including OpenGov (co-founder/CTO) and permitting platform Camino (co-founder/CSO). He holds a degree in Electrical Engineering from Stanford University.",
      initials: "NL",
      imageUrl: nateLevineAsset.url,
    },
  ];


  const founders = teamMembers.filter((m) => m.role.includes("Co-founder"));
  const advisors = teamMembers.filter((m) => m.role === "Advisor");

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Fordje",
    url: "https://www.fordje.com",
    member: teamMembers.map((member) => ({
      "@type": "Person",
      name: member.name,
      jobTitle: member.role,
      description: member.bio,
      image: member.imageUrl,
    })),
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>
      <section className="bg-gradient-to-b from-white to-fordje-blue/5 py-16 md:py-20">
        <div className="container-custom">
          <h2 className="sr-only">Our Team</h2>
          <div className="space-y-12">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {founders.map((member, index) => (
                <TeamMemberCard key={member.name} member={member} animationDelay={0.1 * (index + 1)} />
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl mx-auto">
              {advisors.map((member, index) => (
                <TeamMemberCard key={member.name} member={member} animationDelay={0.1 * (index + 1)} />
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

interface TeamMemberCardProps {
  member: TeamMember;
  animationDelay: number;
}

const TeamMemberCard = ({ member, animationDelay }: TeamMemberCardProps) => {
  return (
    <Card
      className="overflow-hidden animate-fade-in hover:shadow-lg transition-shadow duration-300"
      style={{ animationDelay: `${animationDelay}s` }}
    >
      <div className="p-6 flex flex-col items-center text-center">
        <Avatar className="h-32 w-32 mb-4 border-4 border-fordje-blue/10">
          {member.imageUrl ? (
            <AvatarImage src={member.imageUrl} alt={`Photo of ${member.name}, ${member.role} at Fordje`} />
          ) : (
            <AvatarFallback className="text-2xl bg-gradient-to-br from-fordje-blue to-fordje-accent text-white">
              {member.initials}
            </AvatarFallback>
          )}
        </Avatar>
        <h3 className="text-xl font-semibold mb-1">{member.name}</h3>
        <div className="text-fordje-navy-light mb-4">
          <span>{member.role}</span>
        </div>
      </div>
      <CardContent className="pt-0">
        <p className="text-fordje-navy-light">{member.bio}</p>
      </CardContent>
    </Card>
  );
};

export default TeamSection;
